import {ACTION_PAN, MODE_IDLE, MODE_PANNING} from '../constants';
import {set, getSVGPoint} from './common';
import {fromObject, translate, transform, applyToPoints} from 'transformation-matrix';

// px of pointer drift tolerated before a click on the content turns into a drag
const PAN_THRESHOLD = 5;

/**
 * Atomic pan operation
 * @param value
 * @param SVGDeltaX drag movement
 * @param SVGDeltaY drag movement
 * @param panLimit forces the image to keep at least x pixel inside the viewer
 * @returns {Object}
 */
export function pan(value, SVGDeltaX, SVGDeltaY, panLimit = undefined) {

  let matrix = transform(
    fromObject(value),              //2
    translate(SVGDeltaX, SVGDeltaY) //1
  );

  // panLimit = px of padding to keep between the content and the viewer edges
  if (panLimit != null) {
    let [{x: cx1, y: cy1}, {x: cx2, y: cy2}] = applyToPoints(matrix, [
      {x: value.SVGMinX, y: value.SVGMinY},
      {x: value.SVGMinX + value.SVGWidth, y: value.SVGMinY + value.SVGHeight}
    ]);

    let clampAxis = (min, max, viewer) => {
      if (max - min <= viewer - 2 * panLimit) { // content fits with padding: keep it inside both edges
        if (min < panLimit) return panLimit - min;
        if (max > viewer - panLimit) return (viewer - panLimit) - max;
        return 0;
      }
      // content too big to pad: cover the viewer, at most panLimit at an edge
      if (min > panLimit) return panLimit - min;
      if (max < viewer - panLimit) return (viewer - panLimit) - max;
      return 0;
    };

    let moveX = clampAxis(cx1, cx2, value.viewerWidth);
    let moveY = clampAxis(cy1, cy2, value.viewerHeight);

    matrix = transform(
      translate(moveX, moveY),
      matrix
    )
  }

  return set(value, {
    mode: MODE_IDLE,
    ...matrix,
  }, ACTION_PAN);
}

/**
 * Center the SVG box inside the viewer (spawn position).
 * @param value
 * @returns {Object}
 */
export function center(value) {
  let matrix = fromObject(value);
  let [{x: x1, y: y1}, {x: x2, y: y2}] = applyToPoints(matrix, [
    {x: value.SVGMinX, y: value.SVGMinY},
    {x: value.SVGMinX + value.SVGWidth, y: value.SVGMinY + value.SVGHeight}
  ]);
  matrix = transform(
    translate(
      (value.viewerWidth - (x2 - x1)) / 2 - x1,
      (value.viewerHeight - (y2 - y1)) / 2 - y1
    ),
    matrix
  );
  return set(value, {mode: MODE_IDLE, ...matrix}, ACTION_PAN);
}

/**
 * Start pan operation lifecycle
 * @param value
 * @param viewerX
 * @param viewerY
 * @return {ReadonlyArray<unknown>}
 */
export function startPanning(value, viewerX, viewerY) {
  return set(value, {
    mode: MODE_PANNING,
    panMoved: false,
    startX: viewerX,
    startY: viewerY,
    endX: viewerX,
    endY: viewerY
  }, ACTION_PAN);
}

/**
 * Continue pan operation lifecycle
 * @param value
 * @param viewerX
 * @param viewerY
 * @param panLimit
 * @return {ReadonlyArray<unknown>}
 */
export function updatePanning(value, viewerX, viewerY, panLimit) {
  if (value.mode !== MODE_PANNING) throw new Error('update pan not allowed in this mode ' + value.mode);

  if (!value.panMoved) {
    let driftX = viewerX - value.startX;
    let driftY = viewerY - value.startY;
    if (driftX * driftX + driftY * driftY < PAN_THRESHOLD * PAN_THRESHOLD) return value;
  }

  let {endX, endY} = value;

  let start = getSVGPoint(value, endX, endY);
  let end = getSVGPoint(value, viewerX, viewerY);

  let deltaX = end.x - start.x;
  let deltaY = end.y - start.y;

  let nextValue = pan(value, deltaX, deltaY, panLimit);
  return set(nextValue, {
    mode: MODE_PANNING,
    panMoved: true,
    endX: viewerX,
    endY: viewerY,
  }, ACTION_PAN);
}

/**
 * Stop pan operation lifecycle
 * @param value
 * @return {ReadonlyArray<unknown>}
 */
export function stopPanning(value) {
  return set(value, {
      mode: MODE_IDLE,
      panMoved: false,
      startX: null,
      startY: null,
      endX: null,
      endY: null
    }, ACTION_PAN
  );
}

/**
 * when pointer is on viewer edge -> pan image
 * @param value
 * @param viewerX
 * @param viewerY
 * @return {ReadonlyArray<any>}
 */
export function autoPanIfNeeded(value, viewerX, viewerY) {
  let deltaX = 0;
  let deltaY = 0;

  if (viewerY <= 20) deltaY = 2;
  if (value.viewerWidth - viewerX <= 20) deltaX = -2;
  if (value.viewerHeight - viewerY <= 20) deltaY = -2;
  if (viewerX <= 20) deltaX = 2;

  deltaX = deltaX / value.d;
  deltaY = deltaY / value.d;

  return (deltaX === 0 && deltaY === 0) ? value : pan(value, deltaX, deltaY);
}
